/* eslint-disable @next/next/no-img-element */
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Roboto_Mono } from "next/font/google";
import {
  DeliveredWeeklyVenn,
  DeliveredWeeklyVennStatus,
  DimensionEvent,
  DimensionEventStatus,
} from "@dimensional-engineering/dimensional-models";

import styles from "./WeeklyVenn.module.css";
import WeeklyVennDiagram from "./WeeklyVennDiagram";

import { RootState, useAppDispatch } from "@/redux/store";
import { TimeIconLarge } from "@/components/assessments-new/root/icons/TimeIcon";
import Shimmer from "@/components/shared/Shimmer/Shimmer";
import WarningIcon from "@/components/shared/GhostBox/WarningIcon";
import { useModalContext } from "@/context/ModalContext";
import WeeklyVennModal from "./WeeklyVennModal";
import SadFace from "@/components/shared/GhostBox/SadFace";
import { callFirebaseFunctions } from "@/_firebase/callFirebaseFunctions";
import { setDeliveredWeeklyVenn } from "@/redux/slices/weeklyVennSlice";

const font = Roboto_Mono({ subsets: ["latin"] });
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const WEEKLY_VENN_VIEWED_SLUG_KEY = "weeklyVennViewedSlug";

export default function WeeklyVennHomeCTA() {
  const { setModalComponent } = useModalContext();
  const dispatch = useAppDispatch();
  const { latestVenn, deliveredForLatest, weeklyVennLoaded } = useSelector(
    (state: RootState) => state.weeklyVenn
  );
  const imageUrl = useSelector((state: RootState) => state.user.imageURL);
  const publicProfile = useSelector(
    (state: RootState) => state.user.publicProfileData
  );
  const dimensionEvents = useSelector(
    (state: RootState) => state.user.dimensionEvents
  );

  const [timeLeft, setTimeLeft] = useState<string>("0d:0h:0m:0s");
  const [retrying, setRetrying] = useState(false);
  const hasCompletedCognition = useMemo(
    () => checkHasCompletedCognition(dimensionEvents),
    [dimensionEvents]
  );

  const hasCompleteResult =
    !!deliveredForLatest &&
    deliveredForLatest.status === DeliveredWeeklyVennStatus.complete;

  useEffect(() => {
    if (!deliveredForLatest?.deliveredOn) {
      setTimeLeft("0d:0h:0m:0s");
      return;
    }

    const updateTimer = () => {
      const expiry = deliveredForLatest.deliveredOn + ONE_WEEK_MS;
      const now = Date.now();
      const remainingSeconds = Math.max(Math.floor((expiry - now) / 1000), 0);
      setTimeLeft(formatCountdown(remainingSeconds));
    };

    updateTimer();
    const intervalId = setInterval(updateTimer, 1000);
    return () => clearInterval(intervalId);
  }, [deliveredForLatest?.deliveredOn]);

  if (!weeklyVennLoaded) {
    return (
      <section className={`${styles.main} ${styles.loading}`}>
        <Shimmer />
      </section>
    );
  }

  if (!latestVenn) {
    return (
      <UnavailableVennCard hasCompletedCognition={hasCompletedCognition} />
    );
  }

  const vennTitle = latestVenn.vennName.toUpperCase();
  const viewedSlug = localStorage.getItem(WEEKLY_VENN_VIEWED_SLUG_KEY);
  const isViewed = viewedSlug === latestVenn.slug;

  if (!hasCompleteResult) {
    const message = hasCompletedCognition
      ? "Failed to generate your results. Tap to retry."
      : "Complete the Cognition dimension to unlock this week's Venn.";
    const handleRetry = async () => {
      if (retrying) {
        return;
      }

      setRetrying(true);
      try {
        const retry = callFirebaseFunctions("computeMemberLatestWeeklyVenn");
        const result = await retry();
        dispatch(
          setDeliveredWeeklyVenn(result.data as DeliveredWeeklyVenn | null)
        );
      } catch (error) {
        console.error(error);
      } finally {
        setRetrying(false);
      }
    };

    return (
      <section className={styles.main}>
        <h4 className={`${styles.heading} ${font.className}`}>{vennTitle}</h4>
        <div className={styles.ctaDiagramWrapUnavailable}>
          <div className={styles.ctaDiagramWrap}>
            <WeeklyVennDiagram
              topLabel={latestVenn.topCircleLabel}
              leftLabel={latestVenn.leftCircleLabel}
              rightLabel={latestVenn.rightCircleLabel}
              peerPoints={[]}
              showMyPoint={false}
            />
          </div>
          <div className={styles.ghostBox}>
            <div
              style={{
                padding: !hasCompletedCognition ? "8px 7px 9px 7px" : 0,
              }}
              className={styles.warningIconWrap}
            >
              {!hasCompletedCognition ? <WarningIcon active /> : <SadFace />}
            </div>
            <p className={styles.ghostMessage}>{message}</p>
            {hasCompletedCognition && (
              <button
                type="button"
                className={styles.retryButton}
                disabled={retrying}
                onClick={(event) => {
                  event.stopPropagation();
                  void handleRetry();
                }}
              >
                {retrying ? (
                  <span className={styles.retrySpinner} aria-label="Retrying" />
                ) : (
                  "Retry"
                )}
              </button>
            )}
          </div>
        </div>
        <BottomBar text="Next Venn available soon!" />
      </section>
    );
  }

  return (
    <section
      onClick={() => {
        localStorage.setItem(WEEKLY_VENN_VIEWED_SLUG_KEY, latestVenn.slug);
        setModalComponent(<WeeklyVennModal />);
      }}
      className={styles.main}
      style={isViewed ? {} : { border: "1px solid #2CB0FF" }}
    >
      <h4 className={`${styles.heading} ${font.className}`}>{vennTitle}</h4>
      <div className={styles.ctaDiagramWrap}>
        <WeeklyVennDiagram
          topLabel={latestVenn.topCircleLabel}
          leftLabel={latestVenn.leftCircleLabel}
          rightLabel={latestVenn.rightCircleLabel}
          myPoint={deliveredForLatest}
          myImageUrl={imageUrl || "/default-image.png"}
          myPublicProfile={publicProfile}
          peerPoints={[]}
          avatarSize="medium"
          layout="cta"
        />
      </div>
      <BottomBar text={timeLeft} />
    </section>
  );
}

function UnavailableVennCard(props: { hasCompletedCognition: boolean }) {
  const dispatch = useAppDispatch();
  const [retrying, setRetrying] = useState(false);
  const message = props.hasCompletedCognition
    ? "Failed to generate your results. Tap to retry."
    : "Complete the Cognition dimension to unlock this week's Venn.";
  const handleRetry = async () => {
    if (retrying) {
      return;
    }

    setRetrying(true);
    try {
      const retry = callFirebaseFunctions("computeMemberLatestWeeklyVenn");
      const result = await retry();
      dispatch(
        setDeliveredWeeklyVenn(result.data as DeliveredWeeklyVenn | null)
      );
    } catch (error) {
      console.error(error);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <section className={styles.main}>
      <h4
        className={`${styles.heading} ${styles.unavailableHeading} ${font.className}`}
      >
        VENN
      </h4>
      <div className={styles.ctaDiagramWrapUnavailable}>
        <div className={styles.ghostBox}>
          <div
            style={{
              padding: !props.hasCompletedCognition ? "8px 7px 9px 7px" : 0,
            }}
            className={styles.warningIconWrap}
          >
            {!props.hasCompletedCognition ? (
              <WarningIcon active />
            ) : (
              <SadFace />
            )}
          </div>
          <p className={styles.ghostMessage}>{message}</p>
          {props.hasCompletedCognition && (
            <button
              type="button"
              className={styles.retryButton}
              disabled={retrying}
              onClick={(event) => {
                event.stopPropagation();
                void handleRetry();
              }}
            >
              {retrying ? (
                <span className={styles.retrySpinner} aria-label="Retrying" />
              ) : (
                "Retry"
              )}
            </button>
          )}
        </div>
      </div>
      <BottomBar text="Next Venn available soon!" />
    </section>
  );
}

function BottomBar(props: { text: string }) {
  return (
    <div className={styles.bottomBar}>
      <div className={styles.timeGroup}>
        <TimeIconLarge />
        <p className={styles.timeText}>{props.text}</p>
      </div>
    </div>
  );
}

function checkHasCompletedCognition(
  dimensionEvents: DimensionEvent[] | null
): boolean {
  const cognitionEvent = dimensionEvents?.find(
    (event) => event.dimensionSlug === "personality-II"
  );

  if (!cognitionEvent) {
    return false;
  }

  if (
    cognitionEvent.event === DimensionEventStatus.complete ||
    cognitionEvent.event === DimensionEventStatus.reset
  ) {
    return true;
  }

  if (
    cognitionEvent.event !== DimensionEventStatus.deferred &&
    cognitionEvent.event !== DimensionEventStatus.deferredGated
  ) {
    return false;
  }

  const cognitionLongEvent = dimensionEvents?.find(
    (event) => event.dimensionSlug === "cognition-long"
  );

  if (!cognitionLongEvent) {
    return cognitionEvent.event === DimensionEventStatus.deferred;
  }

  if (cognitionLongEvent.event === DimensionEventStatus.started) {
    return cognitionEvent.event === DimensionEventStatus.deferred;
  }

  return cognitionLongEvent.event !== DimensionEventStatus.resetGated;
}

function formatCountdown(seconds: number) {
  const days = Math.floor(seconds / (24 * 3600));
  const hours = Math.floor((seconds % (24 * 3600)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  return `${days}d:${hours}h:${minutes}m:${remainingSeconds}s`;
}
